// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// ghost.ts — a finished run, small enough to be a link.
//
// THE RECEIVER RE-SIMULATES; IT DOES NOT REPLAY A RECORDING. A shared run is (seed, mode, version,
// claimed score, action log) and the other end runs that log through the REAL shipped rules. If the
// replay does not reproduce the claimed score, the route is not shown as a score — which makes the
// number in the link unforgeable without a server, because the rules are the referee.
//
// AND IT CARRIES A RULES VERSION, which is the part everybody forgets. Any tuning change invalidates
// every link already in the wild, and the naive handling is to call those runs TAMPERED — accusing a
// player of cheating because the game was patched between them sharing it and their friend opening it.
// A version mismatch is reported as "recorded on an older build": the route still draws, the score
// simply is not compared.

import { RULES_VERSION, SLOTS, VERB_SLOTS, Verb } from './tuning';
import { MODE_IDS, modeOf } from './modes';
import { commitTurn, createState, scoreOf, type Action, type State } from './rules';
import { generate } from './gen';

export interface Ghost {
  seed: number;
  mode: string;
  version: number;
  claimed: number;
  log: Action[];
}

export type GhostVerdict =
  | { kind: 'ok'; ghost: Ghost; state: State; score: number }
  | { kind: 'stale'; ghost: Ghost }
  | { kind: 'bad'; why: string };

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function toB64(bytes: number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    const take = Math.min(4, bytes.length - i + 1);
    for (let k = 0; k < take; k++) out += B64[(n >> (18 - k * 6)) & 63];
  }
  return out;
}

function fromB64(str: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < str.length; i += 4) {
    let n = 0;
    let bits = 0;
    for (let k = 0; k < 4 && i + k < str.length; k++) {
      const v = B64.indexOf(str[i + k]);
      if (v < 0) return [];
      n = (n << 6) | v;
      bits += 6;
    }
    n <<= 24 - bits;
    const bytes = Math.floor(bits / 8);
    for (let k = 0; k < bytes; k++) out.push((n >> (16 - k * 8)) & 255);
  }
  return out;
}

/** {verb:4 bits, arg:12 bits} = 2 bytes an action; a thirty-turn run fits in about 420 characters. */
export function encode(g: Ghost): string {
  const bytes: number[] = [];
  const u16 = (n: number): void => {
    bytes.push((n >> 8) & 255, n & 255);
  };
  const u32 = (n: number): void => {
    bytes.push((n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255);
  };
  u32(g.seed >>> 0);
  bytes.push(Math.max(0, MODE_IDS.indexOf(g.mode)));
  u16(g.version & 0xffff);
  u16(Math.max(0, Math.min(65535, g.claimed)));
  u16(g.log.length);
  for (const a of g.log) u16(((a.verb & 15) << 12) | (a.arg & 0xfff));
  return toB64(bytes);
}

export function decode(str: string): Ghost | null {
  const b = fromB64(str.trim());
  if (b.length < 11) return null;
  const seed = ((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0;
  const mode = MODE_IDS[b[4]] ?? MODE_IDS[0];
  const version = (b[5] << 8) | b[6];
  const claimed = (b[7] << 8) | b[8];
  const n = (b[9] << 8) | b[10];
  if (b.length < 11 + n * 2) return null;
  const log: Action[] = [];
  for (let i = 0; i < n; i++) {
    const w = (b[11 + i * 2] << 8) | b[12 + i * 2];
    log.push({ verb: (w >> 12) & 15, arg: w & 0xfff });
  }
  return { seed, mode, version, claimed, log };
}

/**
 * Replay a shared run through the real rules and report what actually happened.
 *
 * `bad` means the log does not reproduce its own claimed score on this build — with the version
 * matching, that is the only thing it can honestly mean. `stale` means the build moved; the route is
 * real, the score is simply not comparable, and nobody is accused of anything.
 */
export function replay(g: Ghost): GhostVerdict {
  const mode = modeOf(g.mode);
  const plan = generate(g.seed, mode);
  if (!plan) return { kind: 'bad', why: 'that building could not be rebuilt' };
  const s = createState(plan.b, mode, g.seed);

  // The log is a flat action list; slot it back into turns exactly as the turn machine would.
  let turn: Action[] = [];
  let spent = 0;
  for (const a of g.log) {
    const cost = VERB_SLOTS[a.verb] || 0;
    if (a.verb === Verb.Hold || spent + cost > SLOTS) {
      commitTurn(s, turn);
      turn = [];
      spent = 0;
      if (a.verb === Verb.Hold) continue;
    }
    turn.push(a);
    spent += cost;
    if (s.over) break;
  }
  if (turn.length && !s.over) commitTurn(s, turn);
  while (!s.over && s.turn < mode.turns) commitTurn(s, []);

  const score = scoreOf(s);
  if (g.version !== RULES_VERSION) return { kind: 'stale', ghost: g };
  if (score !== g.claimed) return { kind: 'bad', why: 'that score does not replay' };
  return { kind: 'ok', ghost: g, state: s, score };
}

/** A run, packed for the URL fragment. Postures are sticky, so they are re-derived rather than stored. */
export function record(seed: number, mode: string, log: readonly Action[], claimed: number): Ghost {
  return { seed, mode, version: RULES_VERSION, claimed, log: log.map((a) => ({ ...a })) };
}

export const linkFor = (g: Ghost): string => `${location.origin}${location.pathname}#g=${encode(g)}`;

export function fromLocation(hash = location.hash): Ghost | null {
  const m = /[#&]g=([A-Za-z0-9\-_]+)/.exec(hash);
  return m ? decode(m[1]) : null;
}
